import React, { useState, useEffect } from "react";
import "./Step2InfoForm.css";

import { ValidatePassword } from "../../../Utils/ValidatePassword";
import { checkEmailAvailability, localRegister } from "../../../Api/Backend/Auth";
import { requestPhoneVerifyCode } from "../../../Api/Backend/CellphoneVerifyCode.js"

const Step2InfoForm = ({ onNextStep, onBackStep, onRegistrationSuccess }) => {
  const [passwordErrorMessage, setPasswordErrorMessage] = useState(null);
  const [registrationError, setRegistrationError] = useState(null);
  const [isEmailAvailable, setIsEmailAvailable] = useState(null); // null - unknown | false - no | true - yes
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [codeRequestCooldown, setCodeRequestCooldown] = useState(0);
  const [isSending, setIsSending] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    gender: "",
    birthYear: "",
    birthMonth: "",
    birthDay: "",
    email: "",
    password: "",
    passwordConfirm: "",
    phoneNumber: "",
    mobileAuthCode: "",
  });

  const handleFormDataChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }))

    if (name === "email") setIsEmailAvailable(null);
  };

  const genders = ["남", "여", "기타"];
  const currentYear = new Date().getFullYear();

  const months = Array.from({ length: 12 }, (_, i) =>
    String(i + 1).padStart(2, "0")
  );

  const days = Array.from({ length: 31 }, (_, i) =>
    String(i + 1).padStart(2, "0")
  );

  const handleEmailCheck = async () => {
    if (!formData.email) return;

    await checkEmailAvailability({
      email: formData.email,
      onSuccess: (isAvailable) => {
        setIsEmailAvailable(isAvailable);
      },
      onError: () => {
        setIsEmailAvailable(null);
      }
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (passwordErrorMessage) {
      setRegistrationError("비밀번호 필드를 확인하세요.");
      return;
    }

    if (!formData.email) {
      setRegistrationError("이메일주소를 입력해 주세요.");
      return;
    }

    if (!showCodeInput) {
      setRegistrationError("휴대폰 번호 인증이 필요합니다.");
      return;
    }

    let available = isEmailAvailable;

    if (available === null) {
      available = await checkEmailAvailability(formData.email);
      setIsEmailAvailable(available);
    }

    if (!available) {
      setRegistrationError("이미 사용 중인 이메일주소입니다.");
      return;
    }

    const missingFields = [];
    if (!formData.password) missingFields.push("비밀번호");
    if (!formData.phoneNumber) missingFields.push("휴대폰번호");
    if (!formData.mobileAuthCode) missingFields.push("인증번호");

    if (missingFields.length > 0) {
      setRegistrationError(`다음 필수 항목을 작성해 주세요: ${missingFields.join(", ")}`);
      return;
    }

    let birthDate = null;
    if (formData.birthYear && formData.birthMonth && formData.birthDay) {
      birthDate = `${formData.birthYear}-${formData.birthMonth}-${formData.birthDay}`;
    }

    const payload = {
      name: formData.name || null,
      birthDate: birthDate,
      gender: formData.gender || null,
      email: formData.email,
      password: formData.password,
      phoneNumber: formData.phoneNumber,
      smsVerificationCode: formData.mobileAuthCode
    }
    console.log("Payload:", payload);

    setIsSending(true);
    await localRegister({
      payload: payload,
      onSuccess: () => {
        setIsSending(false);
        onRegistrationSuccess(formData.email, formData.password);
        onNextStep();
      },
      onError: (error) => {
        setRegistrationError(error.message || "알 수 없는 서버 오류입니다.");
        setIsSending(false);
      }
    })
  };

  useEffect(() => {
    const { password, passwordConfirm } = formData;

    const result = ValidatePassword(password, passwordConfirm);
    setPasswordErrorMessage(result);
  }, [formData.password, formData.passwordConfirm]);


  const handleRequestPhoneVerify = async () => {
    if (!formData.phoneNumber) {
      setRegistrationError("전화번호를 입력해주세요.");
      return;
    }

    await requestPhoneVerifyCode({
      phoneNumber: formData.phoneNumber,
      onSuccess: (data) => {
        setRegistrationError(null);
        setShowCodeInput(true);
        setTimeLeft(180);
        
        setCodeRequestCooldown(30);
      },
      onError: (error) => {
        setRegistrationError(error.message || "휴대폰 번호 인증 중 알 수 없는 오류가 발생했습니다.");
      },
    });
  };

  useEffect(() => {
    if (timeLeft <= 0) return;

    const timerId = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timerId);
  }, [timeLeft]);

  useEffect(() => {
    if (codeRequestCooldown > 0) {
      const timer = setTimeout(() => {
        setCodeRequestCooldown(codeRequestCooldown - 1);
      }, 1000);
  
      return () => clearTimeout(timer);
    }
  }, [codeRequestCooldown]);

  return (
    <div className="signup-step-content">
      {/* <div className="agreement-summary">
        <div className="custom-checkbox-display checked" />
        <label>약관 동의에 대한 내용</label>
        <a href="#">전문보기</a>
      </div> */}

      <div className="info-form-section">
        <form onSubmit={handleSubmit} className="info-form">
          <div className="info-form-title">회원정보 입력</div>
          <table className="info-table">
            <tbody>
              {/* <tr>
                <th>회원구분</th>
                <td>개인회원</td>
              </tr> */}
              <tr>
                <th>이름</th>
                <td>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormDataChange}
                    placeholder="이름을 입력해 주세요"
                  />
                </td>
              </tr>
              <tr>
                <th>생년월일</th>
                <td>
                  <div className="info-table-birthday">
                    <input
                      type="number"
                      name="birthYear"
                      value={formData.birthYear}
                      onChange={handleFormDataChange}
                      min={1900}
                      max={currentYear}
                      placeholder="년도"
                    />

                    <select
                      name="birthMonth"
                      value={formData.birthMonth}
                      onChange={handleFormDataChange}
                    >
                      <option value="">월</option>
                      {months.map((month) => (
                        <option key={month} value={month}>
                          {month}
                        </option>
                      ))}
                    </select>

                    <select
                      name="birthDay"
                      value={formData.birthDay}
                      onChange={handleFormDataChange}
                    >
                      <option value="">일</option>
                      {days.map((day) => (
                        <option key={day} value={day}>
                          {day}
                        </option>
                      ))}
                    </select>
                  </div>
                </td>
              </tr>
              <tr>
                <th>성별</th>
                <td>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleFormDataChange}
                  >
                    <option value="" disabled>
                      성별을 입력해 주세요
                    </option>
                    {genders.map((item, index) => (
                      <option key={index} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
              <tr>
                <th>이메일주소<span className="required-field-label">*</span></th>
                <td>
                  <div className="input-with-button">
                    <input
                      className={`email-input ${isEmailAvailable === null ? "" : isEmailAvailable ? "available" : "unavailable"}`}
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormDataChange}
                      placeholder="이메일주소를 입력해 주세요"
                    />
                    <button
                      type="button"
                      className={`small-button dark ${isEmailAvailable === null ? "" : "disabled"}`}
                      onClick={isEmailAvailable === null ? handleEmailCheck : null}
                    >
                      중복 검사
                    </button>

                  </div>
                  <div
                    className={`email-message ${isEmailAvailable === null ? "" : isEmailAvailable ? "available" : "unavailable"}`}
                  >
                    {isEmailAvailable === true && "사용 가능한 이메일입니다."}
                    {isEmailAvailable === false && "이미 사용 중인 이메일입니다."}
                  </div>
                </td>
              </tr>
              <tr>
                <th>비밀번호<span className="required-field-label">*</span></th>
                <td>
                  <div className="input-group">
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleFormDataChange}
                      placeholder="비밀번호를 입력해 주세요"
                    />
                    <input
                      type="password"
                      name="passwordConfirm"
                      value={formData.passwordConfirm}
                      onChange={handleFormDataChange}
                      placeholder="비밀번호를 한번 더 입력해 주세요"
                    />
                  </div>
                  {passwordErrorMessage &&
                    <div className="info-table-error-msg">{passwordErrorMessage}</div>
                  }
                </td>
              </tr>
              <tr>
                <th>휴대폰번호<span className="required-field-label">*</span></th>
                <td>
                  <div className="input-group">
                    <div className="input-with-button">
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleFormDataChange}
                        placeholder="휴대폰번호를 입력해 주세요"
                      />
                      <button
                        type="button"
                        className={`small-button ${codeRequestCooldown > 0 ? "disabled" : ""}`}
                        onClick={codeRequestCooldown > 0 ? null : handleRequestPhoneVerify}
                      >
                        인증번호 요청
                      </button>
                    </div>

                    {showCodeInput && (
                      <div className="input-with-button">
                        <input
                          type="text"
                          name="mobileAuthCode"
                          value={formData.mobileAuthCode}
                          onChange={handleFormDataChange}
                          placeholder="인증번호를 입력해 주세요"
                        />
                        {/* <button type="button" className="small-button dark">
                          확인
                        </button> */}
                        {timeLeft > 0 ? (
                          <div className="auth-code-timer">
                            {Math.floor(timeLeft / 60).toString().padStart(2, "0")}:
                            {(timeLeft % 60).toString().padStart(2, "0")}
                          </div>
                        ) : (
                          <div className="auth-code-timer">
                            만료됨
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            </tbody>
            <div className="required-field-legend">* 필수 입력 항목</div>
          </table>

          {registrationError !== null &&
            <div className="login-page-error">{registrationError}</div>
          }
          <div className="form-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={onBackStep}
              disabled={isSending}
            >
              취소
            </button>
            <button 
              type="submit" 
              className="submit-button"
              disabled={isSending}
            >
              가입하기
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Step2InfoForm;
